import type { Timestamp } from 'firebase/firestore';

export interface ChatMessageAttachment {
  kind?: 'media' | 'file' | 'link' | 'image' | 'video' | string;
  type?: 'image' | 'video' | 'document' | 'other' | string;
  url: string;
  storage_path?: string;
  file_name?: string;
  mime_type?: string;
  size_bytes?: number;
  size_label?: string;
  file_size?: number | string;
  thumbnail?: string;
  width?: number;
  height?: number;
}

export type FirestoreDateField =
  | Timestamp
  | { toDate: () => Date }
  | string
  | number
  | null
  | undefined;

export type MessagePayloadType =
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'voice'
  | 'pdf'
  | 'file'
  | 'inquiry'
  | 'support'
  | 'package_request'
  | 'package_offer'
  | 'free_call_invitation'
  | 'package_active'
  | 'package_activated';

export interface ChatLinkPreview {
  url: string;
  title: string;
  description: string;
  domain: string;
  display_url?: string;
  type?: 'zoom' | 'youtube' | 'general';
  image?: string;
}

export interface ScheduleDayItem {
  day: string;
  time: string;
}

export interface FormattedTrainerSchedule {
  isUniform: boolean;
  uniformText?: string;
  daysList: ScheduleDayItem[];
  timezone: string;
}

export interface ChatMessage {
  id: string;
  isOptimistic?: boolean;
  body: string;
  message_type: MessagePayloadType | (string & {});
  send_time: FirestoreDateField;
  send_uid: string;
  sender_uid?: string;
  sender_name?: string;
  sender_avatar?: string;
  sender_gender?: string;
  sender_role?: string;
  receive_uid: string;
  receive_name?: string;
  receive_avatar?: string;
  receive_gender?: string;
  receive_role?: string;
  is_read: boolean;
  read_time?: FirestoreDateField;
  read_by?: string;
  chat_id?: string;
  inquiry_id?: string;
  media_url?: string;
  thumbnail_url?: string;
  thumbnail?: string;
  file_name?: string;
  file_size?: number | string;
  size_label?: string;
  mime_type?: string;
  audio_url?: string;
  audio_duration_seconds?: number;
  attachments?: ChatMessageAttachment[];
  link_preview?: ChatLinkPreview;
  link_previews?: ChatLinkPreview[];
  status?: 'new' | 'replied' | 'resolved' | 'reopened';
  isResolve?: boolean;
  resolve_time?: FirestoreDateField;
  inquiryType?: string;
  inquirySubject?: string;
  inquiryDetail?: string;
  programmeName?: string;
  programmeId?: string;
  category?: string;
  inquiry_category?: string;
  inquiry_text?: string;
  status_updated_by?: string;
  status_updated_at?: FirestoreDateField;
  resolved_at?: FirestoreDateField;
  resolved_by?: string;
  // Support ticket & message fields
  subject?: string;
  topic?: string;
  topic_code?: string;
  message?: string;
  is_support?: boolean;
  ticket_id?: string;
  // Package Request & Offer fields
  package_id?: string;
  package_name?: string;
  client_name?: string;
  price?: number | string;
  currency?: string;
  currency_symbol?: string;
  duration?: string;
  start_date?: string;
  offer_status?: 'awaiting_response' | 'accepted' | 'declined' | string;
  whats_included?: string[];
  attached_documents?: Array<{
    id: string;
    title: string;
    type: string;
  }>;
  // Free Call Invitation fields
  call_title?: string;
  call_duration?: string;
  call_status?: 'awaiting_response' | 'confirmed' | string;
  call_date?: string;
  call_time?: string;
  trainer_schedule_summary?: string;
  trainer_timezone?: string;
  trainer_schedule_days?: ScheduleDayItem[];
  intake_data?: {
    gender?: string;
    fitness_goal?: string;
    fitness_level?: string;
    injuries?: string;
    focus_areas?: string;
    training_schedule?: string;
    workout_duration?: string;
    workout_load_level?: string;
    training_location?: string;
    [key: string]: string | undefined;
  };
  // Check-in Mobile Specification fields
  checkinProgress?: CheckinProgress;
  checkinDate?: string;
  checkinTime?: string;
  checkinSelectTimeType?: CheckinSelectTimeType;
  checkinType?: CheckinType;
  checkinNewDate?: string;
  checkinNewTime?: string;
  checkinNewSelectTimeType?: CheckinSelectTimeType;
  checkinNewType?: CheckinType;
  checkinNewSuggest?: boolean;
  checkinDeclinedReason?: string;
  zoomMeetingUrl?: string;
  zoomMeetingId?: string;
  zoomMeetingPwd?: string;
}

export type CheckinProgress = '1' | '2' | '3' | '4' | '5' | '6';
export const CHECKIN_PROGRESS = {
  PENDING: '1',
  NEW_TIME_PROPOSED: '2',
  CONFIRMED: '3',
  ATTENDED: '4',
  DECLINED: '5',
  CANCELLED: '6',
} as const;

export type CheckinSelectTimeType = 'morningList' | 'daytimeList' | 'eveningList';
export type CheckinType = 'inPerson' | 'online';

export function parseFirestoreDate(val: FirestoreDateField): Date {
  if (!val) return new Date();
  if (
    typeof val === 'object' &&
    val !== null &&
    'toDate' in val &&
    typeof (val as { toDate?: () => Date }).toDate === 'function'
  ) {
    return (val as { toDate: () => Date }).toDate();
  }
  if (typeof val === 'object' && val !== null) {
    const sec =
      (val as { seconds?: number; _seconds?: number }).seconds ??
      (val as { _seconds?: number })._seconds;
    if (typeof sec === 'number') {
      const nsec =
        (val as { nanoseconds?: number; _nanoseconds?: number }).nanoseconds ??
        (val as { _nanoseconds?: number })._nanoseconds ??
        0;
      return new Date(sec * 1000 + Math.floor(nsec / 1000000));
    }
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val);
    return isNaN(d.getTime()) ? new Date() : d;
  }
  return new Date();
}

/**
 * Generates a stable private chat document ID by sorting the two UIDs lexicographically.
 */
export function generatePrivateChatId(uid1: string, uid2: string): string {
  const uids = [uid1, uid2].sort();
  return `${uids[0]}_${uids[1]}`;
}

export interface SpecialMessageInfo {
  specialTag: string | null;
  previewText: string;
}

export function getSpecialMessageInfo(
  lastMsg?: { body?: string; messageType?: string } | null,
): SpecialMessageInfo {
  if (!lastMsg) {
    return { specialTag: null, previewText: 'No messages yet' };
  }

  const rawBody = (lastMsg.body || '').trim();
  const rawType = (lastMsg.messageType || '').trim().toLowerCase();

  // Check if body is enclosed in brackets e.g. "[FREE_CALL_INVITATION]"
  let bracketedType = '';
  const bracketMatch = rawBody.match(/^\[([A-Za-z0-9_-]+)\]$/);
  if (bracketMatch) {
    bracketedType = bracketMatch[1].toLowerCase();
  }

  // Determine effective message type
  let effectiveType = rawType;
  if (!effectiveType || effectiveType === 'text') {
    if (bracketedType) {
      effectiveType = bracketedType;
    }
  }

  // Determine special tag
  let specialTag: string | null = null;
  if (effectiveType === 'package_request') {
    specialTag = 'Package request';
  } else if (effectiveType === 'package_offer') {
    specialTag = 'Package offer';
  } else if (effectiveType === 'free_call_invitation' || effectiveType === 'free_call') {
    specialTag = '15-min introductory call';
  } else if (effectiveType === 'package_active' || effectiveType === 'package_activated') {
    specialTag = 'Package active';
  }

  // Determine preview text (NEVER contain brackets)
  let previewText = '';
  if (bracketedType) {
    switch (bracketedType) {
      case 'free_call_invitation':
      case 'free_call':
        previewText = '15-min introductory call invitation';
        break;
      case 'package_request':
        previewText = 'Package request';
        break;
      case 'package_offer':
        previewText = 'Package offer';
        break;
      case 'package_active':
      case 'package_activated':
        previewText = 'Package active';
        break;
      case 'image':
        previewText = 'Photo';
        break;
      case 'video':
        previewText = 'Video';
        break;
      case 'audio':
      case 'voice':
        previewText = 'Voice message';
        break;
      case 'file':
      case 'pdf':
        previewText = 'Document';
        break;
      default:
        previewText = bracketedType.replace(/_/g, ' ');
        break;
    }
  } else if (rawBody && (effectiveType === 'text' || effectiveType === 'support')) {
    previewText = rawBody.replace(/^\[+|\]+$/g, '');
  } else {
    switch (effectiveType) {
      case 'package_request':
        previewText = rawBody || 'Package request';
        break;
      case 'package_offer':
        previewText = 'Package offer';
        break;
      case 'free_call_invitation':
      case 'free_call':
        previewText = '15-min introductory call invitation';
        break;
      case 'package_active':
      case 'package_activated':
        previewText = rawBody || 'Package active';
        break;
      case 'image':
        previewText = 'Photo';
        break;
      case 'video':
        previewText = 'Video';
        break;
      case 'audio':
      case 'voice':
        previewText = 'Voice message';
        break;
      case 'file':
      case 'pdf':
        previewText = (lastMsg as any).file_name || 'Document';
        break;
      default:
        previewText = rawBody.replace(/^\[+|\]+$/g, '') || 'Message';
        break;
    }
  }

  // Final sanitization: strip any surrounding brackets from preview text
  if (previewText.startsWith('[') && previewText.endsWith(']')) {
    previewText = previewText.slice(1, -1).trim();
  }

  return { specialTag, previewText };
}
